import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import { Grid, StatCard, StatValue, StatLabel } from '../../components/shared/StyledComponents'
import { computeHomeStats, HomeStats as Stats } from './homeStats'

const PageTitle = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  font-size: ${({ theme }) => theme.fontSizes.xl};
`

const PageSubtitle = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const WelcomeSection = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primaryLight});
  color: white;
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const WelcomeTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  font-size: ${({ theme }) => theme.fontSizes.lg};
`

const WelcomeText = styled.p`
  margin: 0;
  opacity: 0.9;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
`

// A router Link, not <a href>: a plain anchor reloaded the whole app on every click.
const QuickActionCard = styled(Link)`
  background: ${({ theme }) => theme.colors.secondary};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-align: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`

const ActionIcon = styled.div`
  font-size: 2rem;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const ActionTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`

const ActionDesc = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

export const HomePage: React.FC = () => {
    const { user } = useAuth()
    const [stats, setStats] = useState<Stats>({
        activeOpportunities: 0,
        teamMembers: 0,
        activeProjects: 0,
        pendingOffers: 0,
    })
    const [loading, setLoading] = useState(true)
    const [refreshKey, setRefreshKey] = useState(0)

    // Force refresh on every mount (navigation to this page)
    useEffect(() => {
        setRefreshKey(prev => prev + 1)
    }, [])

    useEffect(() => {
        loadStats()
    }, [refreshKey])

    const loadStats = async () => {
        try {
            setLoading(true)
            const [opportunitiesRes, dashboardRes] = await Promise.all([
                api.opportunities.getAll(),
                api.dashboard.getStats().catch(() => null),
            ])
            const opportunities = Array.isArray(opportunitiesRes.data) ? opportunitiesRes.data : []
            // The team count comes from the stats; only fall back to the user list if they failed.
            const users = dashboardRes
                ? []
                : await api.users.getAll().then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => [])
            setStats(computeHomeStats(opportunities, dashboardRes?.data ?? null, users))
        } catch (error) {
            console.error('Error loading stats:', error)
        } finally {
            setLoading(false)
        }
    }

    const quickActions = [
        { icon: '📋', title: 'Oportunidades', desc: 'Gestionar oportunidades', href: '/oportunidades' },
        { icon: '📊', title: 'Timeline', desc: 'Ver planificación temporal', href: '/timeline' },
        { icon: '📈', title: 'Estadísticas', desc: 'Métricas y disponibilidad', href: '/estadisticas' },
        { icon: '🧩', title: 'Matriz Conocimientos', desc: 'Skills del equipo', href: '/matriz-conocimientos' },
    ]

    return (
        <div>
            <PageTitle>🏠 Panel Principal</PageTitle>
            <PageSubtitle>Resumen y acciones rápidas</PageSubtitle>

            <WelcomeSection>
                <WelcomeTitle>¡Bienvenido, {user?.display_name || 'Usuario'}!</WelcomeTitle>
                <WelcomeText>
                    Desde aquí puedes acceder a todas las funcionalidades de Manager InputKnowledge.
                </WelcomeText>
            </WelcomeSection>

            <h3>📊 Resumen Rápido</h3>
            <Grid columns={4} gap="1rem">
                <StatCard>
                    <StatValue>{loading ? '...' : stats.activeOpportunities}</StatValue>
                    <StatLabel>Oportunidades Activas</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue>{loading ? '...' : stats.teamMembers}</StatValue>
                    <StatLabel>Miembros del Equipo</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue>{loading ? '...' : stats.activeProjects}</StatValue>
                    <StatLabel>Proyectos en Curso</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue>{loading ? '...' : stats.pendingOffers}</StatValue>
                    <StatLabel>Ofertas Pendientes</StatLabel>
                </StatCard>
            </Grid>

            <h3 style={{ marginTop: '2rem' }}>⚡ Acciones Rápidas</h3>
            <QuickActionsGrid>
                {quickActions.map((action) => (
                    <QuickActionCard key={action.title} to={action.href}>
                        <ActionIcon>{action.icon}</ActionIcon>
                        <ActionTitle>{action.title}</ActionTitle>
                        <ActionDesc>{action.desc}</ActionDesc>
                    </QuickActionCard>
                ))}
            </QuickActionsGrid>
        </div>
    )
}
